import React from 'react';
import { StyleSheet, View } from 'react-native';

import { AppBottomSheet, Button, Text } from '~/components/core';
import { useStyles } from '~/hooks';
import { FONT_SIZE_M, FONT_SIZE_S, FONT_SIZE_XS } from '~/constants/typography';
import { scaleSize } from '~/constants/scale';
import { Theme } from '~/theme';

type Props = {
    visible: boolean;
    onClose: () => void;
};

const STEPS = ['build', 'optimize', 'review'];

/**
 * What this feature is, in one sheet. Shown automatically the first time the list is opened
 * (see `useOnboarding` in AIStrategiesList) and on demand from the header's info icon — the
 * same icon-plus-AppBottomSheet explainer pattern used for individual numbers in ResultReveal
 * and StatisticsTab, raised to the level of the feature.
 *
 * Numbered markers rather than per-step icons: the icon fonts are subsetted, and this would
 * otherwise be the only reason to grow the Ionicons subset.
 */
export default function StrategyIntroSheet({ visible, onClose }: Props) {
    const styles = useStyles(stylesGenerator);

    return (
        <AppBottomSheet visible={visible} onClose={onClose} title="ai-strategy-intro-title">
            <Text style={styles.body}>ai-strategy-intro-body</Text>

            {STEPS.map((step, index) => (
                <View key={step} style={styles.stepRow}>
                    <View style={styles.stepMarker}>
                        <Text noTranslate fontType="LATO_BOLD" style={styles.stepNumber}>
                            {String(index + 1)}
                        </Text>
                    </View>
                    <View style={styles.stepText}>
                        <Text fontType="LATO_BOLD" style={styles.stepTitle}>
                            {`ai-strategy-intro-step-${step}-title`}
                        </Text>
                        <Text style={styles.stepBody}>{`ai-strategy-intro-step-${step}-body`}</Text>
                    </View>
                </View>
            ))}

            {/* Everything above sells the optimizer; this keeps a backtest a backtest. */}
            <Text style={styles.disclaimer}>ai-strategy-intro-disclaimer</Text>

            <View style={styles.ctaWrap}>
                <Button title="ai-strategy-intro-dismiss" onPress={onClose} />
            </View>
        </AppBottomSheet>
    );
}

const stylesGenerator = (theme: Theme) =>
    StyleSheet.create({
        body: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
            marginBottom: scaleSize(16),
        },
        stepRow: {
            flexDirection: 'row',
            marginBottom: scaleSize(14),
        },
        stepMarker: {
            width: scaleSize(22),
            height: scaleSize(22),
            borderRadius: scaleSize(11),
            backgroundColor: theme.backgroundSecondary,
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: scaleSize(10),
        },
        stepNumber: {
            fontSize: FONT_SIZE_S,
            color: theme.text,
        },
        stepText: {
            flex: 1,
        },
        stepTitle: {
            fontSize: FONT_SIZE_M,
            color: theme.text,
            marginBottom: scaleSize(2),
        },
        stepBody: {
            fontSize: FONT_SIZE_S,
            color: theme.textSecondary,
        },
        disclaimer: {
            fontSize: FONT_SIZE_XS,
            color: theme.textSecondary,
            fontStyle: 'italic',
            marginTop: scaleSize(2),
            marginBottom: scaleSize(16),
        },
        ctaWrap: {
            marginBottom: scaleSize(4),
        },
    });
